import { useCallback, useEffect, useState } from 'react';
import { Preferences } from '@capacitor/preferences';

function doError(e: any) {
	let message = 'Error getting/updating preferences in usePreferences';
	if ('message' in e) {
		message = message + ' - ' + e.message;
	}
	// eslint-disable-next-line no-console
	console.error(message);
}

/**
 * A wrapper for the capacitor preferences API. It allows async interaction with the API
 * @param { string } key The key to set
 * @param { string | null} value The value to set the key to
 * @returns { Promise<string | null>} The value set, or null if there was an error.
 */
export const setPreference = async (key: string, value: string | null): Promise<string | null> => {
	if (value !== null) {
		try {
			await Preferences.set({ key, value });
			return value;
		} catch (e) {
			doError(e);
			return null;
		}
	} else {
		// Remove the value if we set it to null.
		try {
			await Preferences.remove({ key });
		} catch (e) {
			doError(e);
		}
		return null;
	}
};

/**
 * A wrapper for the capacitor preferences API. It allows async interaction with the API
 * @param {string} key They key to get
 * @returns { Promise<string | null>} The value, or null if there was an error or the value is not set
 */
export const getPreference = async (key: string): Promise<string | null> => {
	try {
		const value = await Preferences.get({ key });
		return value.value;
	} catch (e) {
		doError(e);
		return null;
	}
};

/**
 * A custom hook wrapper for the capacitor preferences API. It allows async interaction with the API
 * and presents a useState like API
 * @param { string } key The key to read/write
 * @returns { [string, function] } The value, and a setter function, if setter is called with null, the value will be removed from the store.
 */
export function usePreferences(key: string): [string | null, (value: string | null) => Promise<string | null>] {
	const [value, setValueInternal] = useState<string | null>(null);

	const setValue = useCallback(
		async (value: string | null) => {
			const val = await setPreference(key, value);
			setValueInternal(val);
			return val;
		},
		[key]
	);

	// Note no dep array, we want to get this on every render, as it could have been changed elsewhere.
	useEffect(() => {
		getPreference(key).then(val => setValueInternal(val));
	});

	return [value, setValue];
}
